import type { components } from "../api/schema";

/** Подписанные Telegram данные пользователя в виде тела `POST /v1/auth/telegram`. */
export type TelegramLoginData = components["schemas"]["TelegramLoginRequest"];

/** Окно входа Telegram Login. В тестах подменяется фейком. */
export type TelegramLogin = {
  /**
   * Заранее загружает виджет: окно Telegram должно открыться прямо в обработчике нажатия,
   * иначе браузер сочтёт его непрошеным и заблокирует.
   */
  prepare: () => void;
  /** Открывает окно Telegram. `null` — пользователь закрыл окно или отказался; ошибка — виджет не загрузился. */
  authorize: () => Promise<TelegramLoginData | null>;
};

// Пользователь, которого виджет отдаёт в callback (https://core.telegram.org/widgets/login#receiving-authorization-data).
type WidgetUser = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
};

type WidgetLogin = {
  auth: (options: { bot_id: string; lang?: string }, callback: (user: WidgetUser | false) => void) => void;
};

declare global {
  interface Window {
    Telegram?: { Login?: WidgetLogin };
  }
}

const WIDGET_URL = "https://telegram.org/js/telegram-widget.js?22";

/**
 * Telegram Login со своей кнопкой вместо iframe виджета. Работает только на домене,
 * привязанном к боту в BotFather (`/setdomain`), поэтому на localhost выключен.
 */
export function createTelegramLogin(botId: string, loadTimeoutMs = 10_000): TelegramLogin {
  let loading: Promise<WidgetLogin> | null = null;

  function load(): Promise<WidgetLogin> {
    loading ??= new Promise<WidgetLogin>((resolve, reject) => {
      const ready = window.Telegram?.Login;
      if (ready) return resolve(ready);
      const script = document.createElement("script");
      script.src = WIDGET_URL;
      script.async = true;
      function failed(message: string) {
        window.clearTimeout(timeout);
        script.onload = null;
        script.onerror = null;
        script.remove();
        reject(new Error(message));
      }
      const timeout = window.setTimeout(() => failed("Telegram Login widget timed out"), loadTimeoutMs);
      script.onload = () => {
        window.clearTimeout(timeout);
        const login = window.Telegram?.Login;
        if (login) resolve(login);
        else failed("Telegram Login widget is unavailable");
      };
      script.onerror = () => {
        // Следующая попытка добавит свой <script>, неудачный не должен копиться в документе.
        failed("Telegram Login widget failed to load");
      };
      document.head.append(script);
    }).catch((error: unknown) => {
      // Следующее нажатие попробует загрузить виджет заново.
      loading = null;
      throw error;
    });
    return loading;
  }

  return {
    prepare() {
      load().catch(() => {});
    },
    async authorize() {
      // The prepared widget opens synchronously inside the tap, including Safari.
      const login = window.Telegram?.Login ?? await load();
      return new Promise((resolve) => {
        login.auth({ bot_id: botId, lang: "ru" }, (user) => resolve(user ? toLoginData(user) : null));
      });
    },
  };
}

/** Backend принимает только поля виджета, а `id` — строкой. Подпись от этого не меняется: она считается по строкам. */
function toLoginData(user: WidgetUser): TelegramLoginData {
  return {
    id: String(user.id),
    first_name: user.first_name,
    last_name: user.last_name,
    username: user.username,
    photo_url: user.photo_url,
    auth_date: user.auth_date,
    hash: user.hash,
  };
}
